// Who registered at this activation, and one of them in full.
//
// ---------------------------------------------------------------------------
// THE AUDIT CAME FIRST AND THIS SCREEN IS SMALLER THAN THE DESIGN BECAUSE OF IT
//
// docs/console-people-activity.md was written before anything was drawn, on
// purpose, because both of these pages are about a named person. Its finding is
// that People can be built as a smaller screen than the one in
// handoff/console-people.jsx, and that four of the design's columns cannot be
// answered by this service at all. Those four are stated on the page, in the
// service's own words, rather than drawn as columns that would always look
// reassuring:
//
//   Passcode not used, as a state a person can be in. The design shows a person
//   the moment an address is typed. This service holds nobody until the
//   registration completes, so there is no such person to list.
//
//   Crew, as a kind of person. There is no attendee kind. `assisted_by` says a
//   crew member helped with a registration, which is a fact about somebody else.
//
//   Withheld, and blocked. No such state exists anywhere. It needs a table and
//   an attributable write, and the audit puts it after these screens.
//
//   Email deliverability. The verification send answers identically whatever
//   becomes of the mail, deliberately, so nothing here knows.
//
// They are not this file's list. xp/operator/src/attendees.ts holds them frozen
// as `cannot_answer` and sends them on every response, so the sentences on the
// screen are the ones the service stands behind and a gap that gets filled in
// later disappears from here without anybody editing a browser. api/attendees.ts
// refuses a response that carries no such list, for the same reason.
//
// ---------------------------------------------------------------------------
// WHAT REPLACED THE THREE COLUMNS THAT COULD NOT BE DRAWN
//
// The design's table is Person, Who, Lane, State, Registered, Email. Person,
// Lane and Registered are real. The other three are not, and none of them is
// left as an empty column:
//
//   Who is gone entirely, because there is nothing weaker to put in its place:
//   every row here is an attendee, so a column would be one repeated word.
//
//   State is replaced by Address, which is the nearest recorded fact and is a
//   narrower claim: whether a passcode sent to that mailbox was ever returned.
//   It is deliberately NOT relabelled "Verified", because on this service most
//   registrations are taken on a stand tablet where nothing was ever sent, and a
//   column reading "not verified" against a whole show's worth of people would
//   be read as a fault.
//
//   Email, which is deliverability, is gone. Nothing knows.
//
// ---------------------------------------------------------------------------
// SEARCH NARROWS THE PAGE IN FRONT OF YOU AND SAYS SO
//
// The design's search box reads the whole fixture, because a fixture is one
// array. The service paginates and offers no search, so the honest console
// filter is over the rows already delivered. Choose.tsx draws that line first
// and Ride.tsx repeats it: a filter sent to the server is a second opinion about
// what may be read, and a filter over rows already on the screen discloses
// nothing that was not on it a moment ago.
//
// What is different here, and is the reason it is worth a paragraph, is that a
// paginated list makes a local filter capable of lying. Somebody who searches a
// surname, sees nothing and concludes that person did not register would be
// wrong on any activation with more than one page. The empty state says which
// rows were read, and the count says how many of this page matched rather than
// how many people matched.
//
// ---------------------------------------------------------------------------
// EVERY ROLE'S, READ ONLY, SINCE THE 11 OCT RULING
//
// These were admin screens until Gabe ruled that a Viewer reads the People
// records of the shows they are granted. `attendeeRecordsRefusal` in
// api/src/reports/routes.ts admits a viewer session that proved a factor to
// both endpoints, and every page and record still files a report_access row
// naming the reader. roleAccess.ts (`people`) opens the page for all three
// roles and check.mjs ties that to the guard. Nothing on either page writes.
//
// ---------------------------------------------------------------------------
// THE READ IS THE DISCLOSURE, SO THERE IS EXACTLY ONE PER SCREEN
//
// The list reads one page. A person's record reads that person and does not read
// the list first, which is why the heading on a record is drawn from the record
// rather than from a row: `report_access` gets one row per page of names and one
// per named person, and a record page that also fetched the list would file a
// disclosure of everybody in order to print one person's name while it loaded.

import { Crumbs, DefList, NoticeBox, PageHead } from "@nova/desk-ui/parts";
import { Empty, SecLabel } from "@nova/desk-ui/kit";
import { Pager } from "@nova/desk-ui/metrics";
import { DeskPage, SortHead, useListSort, type ListSort } from "@nova/desk-ui/page";
import { ListState, Loading } from "@nova/desk-ui/states";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { ATTENDEE_LANE_WORDS, type AttendeeSortKey } from "../../../operator/src/attendees.js";
import {
  PEOPLE_PER_PAGE,
  readAttendee,
  readAttendees,
  type AttendeeConsentEvent,
  type AttendeeGap,
  type AttendeePage,
  type AttendeePasscodeAccount,
  type AttendeeRecord,
  type ConsentMap,
  type ConsentWord,
} from "../api/attendees.js";
import { asMisaddressed, type ApiOptions, type Failure } from "../api/client.js";
import type { SessionToken } from "../session/store.js";
import { ActionHistory, PersonActions, StandingTags } from "../people/PersonActions.js";
import { ReadFailed } from "./ReadFailed.js";
import { Refusal } from "./Refusal.js";
import { useShow } from "./Show.js";

export type PeopleProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  /** Null on the list. Set on one person. */
  readonly attendeeRef: string | null;
  readonly onOpen: (attendeeRef: string) => void;
  readonly onList: () => void;
  readonly onSessionEnded: (detail: string) => void;
  readonly role: string;
  readonly onEnrol: () => void;
};

/**
 * The design's lede with its first clause corrected.
 *
 * PeopleList writes "Everybody who has started a registration against this
 * activation". That is the audit's first finding stated as a fact on a screen:
 * this service holds no person until a registration completes, and an address
 * somebody typed and abandoned exists only as a digest that is swept within a
 * day. The rest of the sentence is the design's and is true.
 *
 * The second sentence is the design's own, moved up from the search panel. It
 * belongs at the top rather than beside a text box, because on this console it
 * is the page read that is recorded and not the typing.
 */
const LIST_LEDE =
  "Completed registrations for this activation. Opening this list is recorded against your sign-in.";

export function People({
  api,
  token,
  activationId,
  attendeeRef,
  onOpen,
  onList,
  onSessionEnded,
  role,
  onEnrol,
}: PeopleProps): ReactNode {
  // TWO COMPONENTS RATHER THAN ONE WITH A BRANCH INSIDE IT, so that the effect
  // that reads a page of names does not exist at all on a record page. A single
  // component with both effects and a guard is one edit away from filing a
  // disclosure of everybody every time somebody opens one person.
  if (attendeeRef !== null) {
    return (
      <One
        api={api}
        token={token}
        activationId={activationId}
        attendeeRef={attendeeRef}
        onList={onList}
        onSessionEnded={onSessionEnded}
        role={role}
        onEnrol={onEnrol}
      />
    );
  }

  return (
    <Many
      api={api}
      token={token}
      activationId={activationId}
      onOpen={onOpen}
      onSessionEnded={onSessionEnded}
      role={role}
      onEnrol={onEnrol}
    />
  );
}

// ------------------------------------------------------------------- the list

type Paged =
  | { readonly status: "reading" }
  | { readonly status: "read" }
  | { readonly status: "failed"; readonly failure: Failure };

/** All, or the two answers `email_verified_at` can honestly be filtered on. */
type Proved = "All" | "Proved" | "Not proved";

/**
 * The list's order before anybody presses a head: the order people arrived in,
 * which is registered, oldest first, near enough that a screen reader is told
 * so. It is the service's own spine order rather than the clock's, so a tablet
 * that synced late can sit a row out; pressing Registered asks for the clock.
 */
const PEOPLE_REST: ListSort<AttendeeSortKey> = { key: "registered", dir: "asc" };

/**
 * Which way a column runs the first time its head is pressed. Registered is
 * newest first, because that is who somebody at a show is looking for; the
 * words and Address (not proved first, the list somebody is chasing) start
 * ascending.
 */
const PEOPLE_FIRST: Readonly<Partial<Record<AttendeeSortKey, "asc" | "desc">>> = {
  registered: "desc",
};

function Many({
  api,
  token,
  activationId,
  onOpen,
  onSessionEnded,
  role,
  onEnrol,
}: {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly onOpen: (attendeeRef: string) => void;
  readonly onSessionEnded: (detail: string) => void;
  readonly role: string;
  readonly onEnrol: () => void;
}): ReactNode {
  const show = useShow();
  const [paged, setPaged] = useState<Paged>({ status: "reading" });
  // Bumped by "Ask again"; the read below runs again whenever it changes.
  const [attempt, setAttempt] = useState(0);
  /**
   * The last page the service actually handed over.
   *
   * KEPT ACROSS A PAGE TURN, WHICH IS WHAT KEEPS THE SEARCH PUT. A frame that
   * unmounted the bar while the next page was being fetched would take the
   * filter off the screen exactly while the rows under it changed. states.tsx
   * makes the same argument about a filter bar that disappears mid-read.
   *
   * The rows themselves are not drawn from it while a read is in flight: the
   * table area is `reading`, so nobody sees page one's people under a pager
   * saying page two.
   */
  const [held, setHeld] = useState<AttendeePage | null>(null);
  const [per, setPer] = useState(PEOPLE_PER_PAGE);
  const [page, setPage] = useState(1);
  const [find, setFind] = useState("");
  const [proved, setProved] = useState<Proved>("All");
  // THE ORDER IS THE SERVICE'S, ASKED FOR AND NOT APPLIED HERE (12 Oct
  // hand-off: every column sorts, on the server). A page of a paginated list
  // re-sorted in a browser is twenty five people in order and calling it the
  // list; the service orders the whole subset and hands back this page of it.
  const order = useListSort<AttendeeSortKey>(PEOPLE_REST, PEOPLE_FIRST);
  const chosen = order.lit ? order.sort : null;
  const chosenKey = chosen?.key ?? null;
  const chosenDir = chosen?.dir ?? null;
  const { onSort: pressHead } = order;
  // A new order starts at its own first page. Page three of one order is
  // nobody in particular in another.
  const sortBy = useCallback(
    (key: AttendeeSortKey) => {
      pressHead(key);
      setPage(() => 1);
    },
    [pressHead],
  );

  useEffect(() => {
    // The frame's directory read goes first. Ride.tsx waits the same way and for
    // the same reason: two reads started in one tick arrive in whatever order
    // the network settles them in, and this one hands over names.
    if (show.status !== "ready") return;

    let cancelled = false;
    setPaged({ status: "reading" });

    void (async () => {
      const result = await readAttendees(
        api,
        token,
        activationId,
        (page - 1) * per,
        per,
        chosenKey === null || chosenDir === null ? null : { key: chosenKey, dir: chosenDir },
      );
      if (cancelled) return;
      if (!result.ok) {
        setPaged({ status: "failed", failure: result });
        // A 401 is the one refusal that ends a session. A 403 does not, and
        // signing somebody out for asking to read something they may not read
        // is the mistake client.ts names.
        if (result.kind === "session_ended") onSessionEnded(result.detail);
        return;
      }
      setHeld(result.value);
      setPaged({ status: "read" });
    })();

    return () => {
      cancelled = true;
    };
  }, [api, token, activationId, onSessionEnded, show.status, page, per, attempt, chosenKey, chosenDir]);

  // THE DESIGN'S FILTERS, IN THE RIGHT-HAND COLUMN (12 Oct hand-off;
  // console-people.jsx draws them as `.filters.side`). Drawn while the first
  // page is read as well as after: a column that only appears once there are
  // rows moves the page under the reader. `totals` is the count line under them,
  // which the hand-off puts in the same column.
  const filters = (totals: ReactNode) => (
    <div className="filters">
      <label className="field">
        <span className="label">Search</span>
        <input
          className="input"
          type="search"
          value={find}
          onChange={(event) => setFind(event.target.value)}
          placeholder="Name, address or record id"
        />
      </label>
      {/* THE DESIGN'S "STATE" AND "WHO" ARE BOTH GONE AND THIS IS NEITHER OF
          THEM. Its State offers Verified, Passcode not used, Withheld and
          Blocked. Passcode not used does not exist and Verified is a stronger
          word than the data supports. Withheld and Blocked exist since 152 and
          are tagged on each row; a filter by them would narrow one page rather
          than the list, so it is not offered. Its Who offers Attendee and Crew, and there is
          no attendee kind. What is filterable is whether a passcode sent to
          that mailbox came back. */}
      <label className="field">
        <span className="label">Address</span>
        <select className="input" value={proved} onChange={(event) => setProved(event.target.value as Proved)}>
          <option value="All">All addresses</option>
          <option value="Proved">Proved</option>
          <option value="Not proved">Not proved</option>
        </select>
      </label>
      {totals}
    </div>
  );

  const head = <PageHead title="People" lede={LIST_LEDE} uuid={activationId} explain="info" />;

  // A FAILED READ IS DRAWN IN THE PAGE, the design's way (console-people.jsx,
  // ListState; desks.mdc): the head, the filters and the rail stay, and the
  // kit's Failed with Ask again stands where the list would be. It used to
  // replace the whole page with a refusal under a heading of its own. Ask
  // again asks for whichever read failed: the show's own, or this page.
  const failed =
    show.status === "failed"
      ? {
          failure: show.failure,
          retry: show.reload,
          notCounted: "The list did not load. This does not mean nobody registered.",
        }
      : paged.status === "failed"
        ? {
            failure: paged.failure,
            retry: () => setAttempt((n) => n + 1),
            notCounted: "This page did not load. This does not mean nobody registered.",
          }
        : null;

  if (failed !== null) {
    return (
      <DeskPage head={head} side={filters(null)}>
        <section>
          <SecLabel>Everybody</SecLabel>
          <ReadFailed
            what="Could not load people"
            failure={failed.failure}
            role={role}
            onEnrol={onEnrol}
            notCounted={failed.notCounted}
            onRetry={failed.retry}
          />
        </section>
      </DeskPage>
    );
  }

  if (held === null) {
    return (
      <DeskPage head={head} side={filters(null)}>
        <section>
          <SecLabel>Everybody</SecLabel>
          {/* Five columns because that is what the table below has. A skeleton
              with the wrong number of them is the jump it exists to prevent. */}
          <Loading rows={5} cols={5} label="Loading people…" />
        </section>
      </DeskPage>
    );
  }

  const term = find.trim().toLowerCase();
  const matched = held.attendees.filter((line) => {
    if (proved === "Proved" && line.emailVerifiedAt === null) return false;
    if (proved === "Not proved" && line.emailVerifiedAt !== null) return false;
    if (term === "") return true;
    // Name, address and record id. The design searches a company as well, which
    // is a configured form field rather than a column: the list read carries no
    // `fields`, so a company search here would match nothing and read as a
    // search that found nobody.
    return `${line.displayName ?? ""} ${line.contactEmail ?? ""} ${line.attendeeRef}`
      .toLowerCase()
      .includes(term);
  });
  // In the order the service sent them. The narrowing above drops rows and
  // moves none.
  const rows = matched;
  const narrowed = term !== "" || proved !== "All";
  const wholeSubset = held.page.total === held.page.returned;
  const heads = { sort: order.sort, lit: order.lit, onSort: sortBy };

  // The totals, in the column with the filters. The service's count of the
  // whole subset, and how many of this page the filters left, which is a
  // different number and is labelled as one: the search reads this page only.
  const totals = (
    <p className="help help-tight">
      {held.page.total} {held.page.total === 1 ? "person" : "people"}
      {narrowed ? ` · ${rows.length} of this page ${rows.length === 1 ? "matches" : "match"}` : ""}
    </p>
  );

  return (
    <DeskPage
      head={head}
      side={filters(totals)}
      // THE PAGER TURNS THE SERVICE'S PAGE RATHER THAN SLICING AN ARRAY, which
      // is the one way this control differs from most uses of it on this
      // console. `total` is the service's count of the whole subset, so the
      // line under it is true; pressing next files another `report_access`
      // row, because another page of names is another disclosure.
      pager={
        held.page.total === 0 ? null : (
          <Pager page={page} per={per} setPer={setPer} setPage={setPage} total={held.page.total} unit="people" />
        )
      }
      foot={
        <Gaps
          label="Not available"
          lede="This information is not collected, so it is not shown."
          gaps={held.cannotAnswer}
        />
      }
    >
      {/* The label straight over the table, with no row of its own around it:
          it is what lets the right-hand column line up with the table's top
          (furniture.css's `.desk-cols:has()` rule reads a section that opens
          on a label). The filters that used to share its row are in the
          column now. */}
      <section>
        <SecLabel info="Each page view is recorded. Lane shows where the registration was completed. Open a person to see their consent.">
          {narrowed ? "What matches on this page" : wholeSubset ? "Everybody" : "This page"}
        </SecLabel>

        <ListState
          state={paged.status === "reading" ? "reading" : "ready"}
          rows={rows}
          cols={5}
          unit="people"
          empty={
            held.page.total === 0 ? (
              // THE DESIGN'S EMPTY STATE SAYS THE OPPOSITE OF THIS AND IS THE
              // audit's first finding in miniature: "A person appears here the
              // moment an address is entered, before the passcode is used." They
              // do not. Repeating it would tell somebody staring at an empty
              // screen that registrations are being started and lost, which is a
              // support call about a fault that does not exist.
              <Empty title="Nobody has completed a registration here">
                <p>People appear here after they finish registration.</p>
              </Empty>
            ) : (
              <Empty title="No one on this page matches">
                <p>
                  Search only checks this page. Clear the filter or try another page.
                </p>
              </Empty>
            )
          }
        >
          <>
            <div className="tablewrap">
              <div className="tablescroll">
                <table>
                  <thead>
                    {/* Every column sorts, and the service does the sorting:
                        the keys are ATTENDEE_SORT_KEYS, which it refuses any
                        other than. Open is a control, not a column. */}
                    <tr>
                      <SortHead of="person" {...heads}>
                        Person
                      </SortHead>
                      <SortHead of="lane" {...heads}>
                        Lane
                      </SortHead>
                      <SortHead of="address" {...heads}>
                        Address
                      </SortHead>
                      <SortHead of="registered" {...heads}>
                        Registered
                      </SortHead>
                      <th scope="col">
                        <span className="vh">Open</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((line) => (
                      // The design's whole-row open, by pointer or by Enter.
                      <tr
                        key={line.attendeeRef}
                        className="row-go"
                        tabIndex={0}
                        onClick={() => onOpen(line.attendeeRef)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && event.target === event.currentTarget) onOpen(line.attendeeRef);
                        }}
                      >
                        <td data-label="Person">
                          <b>{line.standing.erased ? "Details erased" : (line.displayName ?? "No name given")}</b>
                          {/* Where an Admin's actions have left them (152). */}
                          {line.standing.erased || line.standing.withheld || line.standing.blocked ? (
                            <>
                              {" "}
                              <StandingTags standing={{ ...line.standing, corrected: false }} />
                            </>
                          ) : null}
                          {/* The address under the name, which is the design's
                              second line and is also one of the two things the
                              search above reads. A registration with no contact
                              address is a real shape: `deliver_to_email` is a
                              separate slot and a stand tablet need not collect
                              either. */}
                          <p className="t-note mono">
                            {line.contactEmail ?? (
                              <span className="quiet">{line.standing.erased ? "erased" : "no address given"}</span>
                            )}
                          </p>
                        </td>
                        <td data-label="Lane">
                          <Lane of={line.captureChannel} />
                        </td>
                        <td data-label="Address">
                          <Address proved={line.emailVerifiedAt} assurance={line.identityAssurance} />
                        </td>
                        <td data-label="Registered" className="num">
                          {when(line.registeredAt)}
                        </td>
                        <td data-label="">
                          <button
                            type="button"
                            className="btn btn-quiet btn-sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              onOpen(line.attendeeRef);
                            }}
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        </ListState>
      </section>
    </DeskPage>
  );
}

// ----------------------------------------------------------------- one person

type Person =
  | { readonly status: "reading" }
  | { readonly status: "read"; readonly value: AttendeeRecord }
  | { readonly status: "failed"; readonly failure: Failure };

function One({
  api,
  token,
  activationId,
  attendeeRef,
  onList,
  onSessionEnded,
  role,
  onEnrol,
}: {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly attendeeRef: string;
  readonly onList: () => void;
  readonly onSessionEnded: (detail: string) => void;
  readonly role: string;
  readonly onEnrol: () => void;
}): ReactNode {
  const show = useShow();
  const [person, setPerson] = useState<Person>({ status: "reading" });
  // Bumped by "Ask again"; the read below runs again whenever it changes.
  const [attempt, setAttempt] = useState(0);
  // What the last person action said, drawn over the record it changed.
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    if (show.status !== "ready") return;

    let cancelled = false;
    // An action re-reads the record rather than patching it, and keeps what is
    // on screen while it does, so the notice and the record arrive together.
    setPerson((now) => (now.status === "read" ? now : { status: "reading" }));

    void (async () => {
      const result = await readAttendee(api, token, activationId, attendeeRef);
      if (cancelled) return;
      if (!result.ok) {
        // The reference came out of the address bar, so the service refusing to
        // read it is a half-copied link rather than a request this console got
        // wrong, and the two want different sentences.
        setPerson({ status: "failed", failure: asMisaddressed(result) });
        if (result.kind === "session_ended") onSessionEnded(result.detail);
        return;
      }
      setPerson({ status: "read", value: result.value });
    })();

    return () => {
      cancelled = true;
    };
  }, [api, token, activationId, attendeeRef, onSessionEnded, show.status, attempt]);

  if (person.status === "failed") {
    return (
      <div className="stack">
        <Away onList={onList} name={null} />
        <Refusal
          onRetry={() => setAttempt((n) => n + 1)}
          what="Could not load this person"
          failure={person.failure}
          role={role}
          onEnrol={onEnrol}
          notCounted="No personal record is shown because this link did not load."
        />
      </div>
    );
  }

  if (person.status === "reading") {
    return (
      <div className="stack">
        <Away onList={onList} name={null} />
        <header className="head">
          <h1>Loading this person…</h1>
          <p className="lede">Registration, consent and email-code history.</p>
          <p className="id people-uuid">{attendeeRef}</p>
        </header>
        <Loading rows={4} cols={2} />
      </div>
    );
  }

  const { registration, consent, standing } = person.value;
  const heading = standing.erased ? "Details erased" : (registration.displayName ?? "No name given");

  return (
    <div className="stack">
      <Away onList={onList} name={heading} />

      <header className="head">
        {/* The design's heading is the name and its lede is the address, and both
            are kept. A registration with no name is drawn as having none rather
            than falling back to the address: the two are different facts, and a
            page headed with an address looks like a person who gave one name. */}
        <h1>{heading}</h1>
        <p className="lede mono">
          {standing.erased
            ? "An Admin erased this person's details. Their registration is kept in the counts."
            : (registration.contactEmail ?? "No contact address on this registration")}
        </p>
        <div className="acts acts-spaced">
          <StandingTags standing={standing} />
          <Address proved={registration.emailVerifiedAt} assurance={registration.identityAssurance} />
          <span className="tag">
            <Lane of={registration.captureChannel} />
          </span>
          {/* THE DESIGN'S THIRD TAG IS THE PERSON'S KIND AND THERE IS NO SUCH
              THING. This one is the nearest recorded fact and says whose it is:
              a crew member assisted, which is not the person being crew. The gap
              list below says the same thing at length. */}
          {registration.assistedBy === null ? null : (
            <span className="tag">A crew member assisted</span>
          )}
        </div>
      </header>

      <section>
        <SecLabel info="Extra answers appear below when this activation asked for them. Email verification is not required on a stand tablet.">
          Registration
        </SecLabel>
        <DefList
          rows={[
            ["Record id", <span className="people-uuid mono">{person.value.attendeeRef}</span>],
            ["Name", or(registration.displayName)],
            ["Email address", or(registration.contactEmail)],
            // A typed slot of its own rather than a copy of the address above.
            // Send routes assets on this one, so a record where the two differ is
            // a record where a file went somewhere the attendee did not read.
            ["Address anything is sent to", or(registration.deliverToEmail)],
            ["Phone", or(registration.phone)],
            ["Registered", when(registration.registeredAt)],
            [
              "Address proved",
              registration.emailVerifiedAt === null ? (
                <span className="quiet">
                  no: nothing was ever sent to this address and returned
                </span>
              ) : (
                when(registration.emailVerifiedAt)
              ),
            ],
            ["Marketing consent", <Consent word={consent["marketing"]} />],
            ["Client transfer", <Consent word={consent["client_data_transfer"]} />],
          ]}
        />
        {/* THE DESIGN'S OTHER THREE ROWS, AND WHERE EACH ONE WENT. Company and
            Country are not columns on this service: the audit puts both in
            `payload.fields`, a configurable slot, so they are in the form section
            below and only for an activation whose form asked. Email
            deliverability is the fourth gap and is stated with the others. */}
      </section>

      <section>
        <SecLabel info="This records the channel and proof used when registration finished. Self asserted means the email was typed but not verified.">
          How they registered
        </SecLabel>
        <DefList
          rows={[
            ["Lane", <Lane of={registration.captureChannel} />],
            ["Where they were", <Word of={registration.surface} words={SURFACE} />],
            ["How it was taken", <Word of={registration.captureMode} words={CAPTURE_MODE} />],
            [
              "Helped by",
              registration.assistedBy ?? <span className="quiet">nobody is recorded as helping</span>,
            ],
            ["Assurance recorded", <Word of={registration.identityAssurance} words={ASSURANCE} />],
            ["Form version", number(registration.fieldSetVersion)],
            ["Captured under configuration", number(registration.capturedUnderConfigVersion)],
          ]}
        />
      </section>

      <FormFields fields={registration.fields} />

      <Agreed
        consent={consent}
        events={person.value.consentEvents}
        note={person.value.consentNote}
      />

      <Passcodes account={person.value.passcodes} />

      <Gaps
        label="Not available"
        lede="This information is not recorded."
        gaps={person.value.cannotAnswer}
      />

      {/* Beside the actions rather than at the top: the reader is down here,
          at the button they just pressed. */}
      {done === null ? null : (
        <NoticeBox kind="good" title="Recorded">
          <p>{done}</p>
        </NoticeBox>
      )}

      <ActionHistory actions={person.value.actions} note={person.value.standingNote} />

      <PersonActions
        api={api}
        token={token}
        activationId={activationId}
        attendeeRef={person.value.attendeeRef}
        role={role}
        standing={standing}
        registration={registration}
        erasure={person.value.erasure}
        onDone={(said) => {
          setDone(() => said);
          setAttempt((n) => n + 1);
        }}
        onSessionEnded={onSessionEnded}
      />

      <p className="help help-head">
        Read at {when(person.value.asAt)}. {person.value.accessNote}
      </p>
    </div>
  );
}

/**
 * The one way off a person's page: the October hand-off's breadcrumbs, People
 * and then whoever this is, in place of the "← Back to people" button.
 *
 * `name` is null until the record has been read (and when it could not be),
 * and the crumb then says "Person" rather than borrowing the heading's
 * "Loading this person…": a breadcrumb is where you are, and that is a person
 * whose name is not known yet, not a person called Loading.
 */
function Away({ onList, name }: { readonly onList: () => void; readonly name: string | null }): ReactNode {
  return <Crumbs items={[["People", onList], [name ?? "Person", null]]} />;
}

/**
 * Whatever this activation's form asked for, as it was captured.
 *
 * NOTHING AT ALL WHEN THE FORM ASKED FOR NOTHING. The September design drew a
 * panel saying "No extra registration fields were recorded"; the October one
 * cut the sentence and left the panel standing empty, which is a box with
 * nothing in it under a heading promising fields. Neither is drawn: a record
 * from a form with no extra fields has no section for them, the way it has no
 * section for anything else the form did not have.
 *
 * THE DESIGN'S SENTENCE IS KEPT AND SHARPENED. It says a field nobody was asked
 * reads "not given" rather than empty, which conflates two different things: a
 * field this form never had is not here at all, and a field it had and nobody
 * answered is here with nothing in it. Both are drawn, and the note says which
 * is which, because "the form did not ask" and "they declined to say" are
 * opposite answers to a client wondering why a column is thin.
 */
function FormFields({
  fields,
}: {
  readonly fields: readonly { readonly name: string; readonly value: string }[];
}): ReactNode {
  if (fields.length === 0) return null;
  return (
    <section>
      <SecLabel info="Only fields shown on the registration form are listed. Blank and not asked are different.">
        Registration fields
      </SecLabel>
      <DefList
        rows={fields.map(
          (field) =>
            [
              field.name,
              field.value === "" ? <span className="quiet">asked, not answered</span> : field.value,
            ] as const,
        )}
      />
    </section>
  );
}

/**
 * What this person agreed to: the standing position, then every act behind it.
 *
 * THE MAP AND THE HISTORY ARE BOTH DRAWN, and neither is derived here from the
 * other. The service builds the map from the events it has just read, in one
 * function shared with the export, so the summary and the history cannot
 * disagree about whether somebody consented. Recomputing it in a browser would
 * be a second answer to that question, and the two would differ the first time
 * somebody's withdrawal arrived out of order.
 *
 * `consent_note` is drawn verbatim and is the most important sentence in this
 * section. There is no event for a refusal, so a purpose nobody was asked about
 * and a purpose everybody declined are indistinguishable from here, and an
 * absent purpose has to be read as unknown rather than as no.
 */
function Agreed({
  consent,
  events,
  note,
}: {
  readonly consent: ConsentMap;
  readonly events: readonly AttendeeConsentEvent[];
  readonly note: string;
}): ReactNode {
  const purposes = Object.keys(consent).sort();

  return (
    <section>
      <SecLabel info="The exact wording in effect when each choice was made is shown here. Later edits do not change this record.">
        Consent
      </SecLabel>

      {purposes.length === 0 ? (
        <NoticeBox kind="amber" title="No consent recorded">
          <p>
            No choice was recorded. This does not show whether the person declined or was never asked.
          </p>
        </NoticeBox>
      ) : (
        <DefList
          rows={purposes.map(
            (purpose) => [purposeRead(purpose), <Consent word={consent[purpose]} />] as const,
          )}
        />
      )}

      {events.length === 0 ? null : (
        // HAND WRITTEN RATHER THAN DataTable, WHICH THE DESIGN USES HERE. That
        // part puts `.num` on every column but the first, and `.num` is mono with
        // `white-space: nowrap`. The last column carries the paragraph this
        // person was shown, so it would be one unwrappable line as wide as the
        // wording, pushing the whole record sideways. The timestamp is the only
        // column that wants the numeric face.
        <div className="tablewrap people-consents">
          <div className="tablescroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Purpose</th>
                  <th scope="col">Said</th>
                  <th scope="col">When</th>
                  <th scope="col">Where and in what words</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, at) => (
                  <tr key={`${event.at}:${event.purpose ?? ""}:${at}`}>
                    <td data-label="Purpose">
                      {event.purpose === null ? (
                        <span className="unk">no purpose on the event</span>
                      ) : (
                        purposeRead(event.purpose)
                      )}
                    </td>
                    <td data-label="Said">
                      {event.event === "consent_withdrawn" ? (
                        <span className="tag tag-warn">Taken back</span>
                      ) : (
                        <span className="tag tag-good">Agreed</span>
                      )}
                    </td>
                    <td data-label="When" className="num">
                      {when(event.at)}
                    </td>
                    <td data-label="Where and in what words">
                      <p className="people-said">
                        <Lane of={event.captureChannel} />
                        {event.method === null ? null : <> · {methodRead(event.method)}</>}
                      </p>
                      {event.wording === null ? (
                        <p className="t-note">
                          {event.event === "consent_withdrawn"
                            ? "No wording is attached to this withdrawal."
                            : "The original wording is unavailable."}
                        </p>
                      ) : (
                        <>
                          <p className="people-wording">{event.wording.body}</p>
                          <p className="t-note id people-uuid">
                            {event.wording.locale === ""
                              ? event.wording.wordingHash
                              : `${event.wording.locale} · ${event.wording.wordingHash}`}
                          </p>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* The design's own sentence, then the service's. The first is true and
          worth keeping; the second is the one nobody may paraphrase. */}
      <p className="help help-head">{note}</p>
    </section>
  );
}

/**
 * The passcode history, or the reason there is not one.
 *
 * THE THREE STATES ARE DRAWN AS THREE STATES AND THIS IS THE WHOLE SECTION.
 * attendees.ts returns a union rather than a list that is sometimes empty,
 * because 114 holds an HMAC of the address instead of the address: an instance
 * with no pepper and a registration with no address can each be answered only
 * with "this was not looked up", and an empty table would say something quite
 * different and much worse, that nobody ever sent this person a passcode.
 *
 * The design's table is When / What happened / Where, built from a feed of
 * issued, used and expired events. Two of those three cannot be drawn: nothing
 * writes a row when a passcode expires, and the verification table holds no
 * surface and no station. So a row here is one passcode with its own timestamps,
 * and both absences are in the gap list rather than being drawn as blanks.
 */
function Passcodes({ account }: { readonly account: AttendeePasscodeAccount }): ReactNode {
  return (
    <section>
      <SecLabel info="The codes themselves are never stored. Only their timing and status appear here.">
        Email verification codes
      </SecLabel>

      {account.kind === "not_looked_up" ? (
        <NoticeBox kind="amber" title="No code history available">
          <p>{account.says}</p>
        </NoticeBox>
      ) : account.passcodes.length === 0 ? (
        <Empty title="No verification code sent to this email">
          <p>{account.says}</p>
        </Empty>
      ) : (
        <>
          <div className="tablewrap">
            <div className="tablescroll">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Issued</th>
                    <th scope="col">Due to stop working</th>
                    <th scope="col">Address proved</th>
                    <th scope="col">Replaced by a resend</th>
                    <th scope="col">Spent on a registration</th>
                  </tr>
                </thead>
                <tbody>
                  {account.passcodes.map((passcode) => (
                    <tr key={passcode.verificationId}>
                      <td data-label="Issued" className="num">
                        {when(passcode.issuedAt)}
                      </td>
                      <td data-label="Due to stop working" className="num">
                        {when(passcode.expiresAt)}
                      </td>
                      <td data-label="Address proved">
                        {passcode.consumedAt === null ? (
                          <span className="quiet">never spent</span>
                        ) : (
                          when(passcode.consumedAt)
                        )}
                      </td>
                      <td data-label="Replaced by a resend">
                        {passcode.supersededAt === null ? (
                          <span className="quiet">not replaced</span>
                        ) : (
                          when(passcode.supersededAt)
                        )}
                      </td>
                      <td data-label="Spent on a registration">
                        {passcode.ticketSpentAt === null ? (
                          <span className="quiet">not spent</span>
                        ) : (
                          when(passcode.ticketSpentAt)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="help help-head">{account.says}</p>
        </>
      )}

      {/* The design's own caption, kept because it is true and because it is the
          question somebody asks of this table first. */}
    </section>
  );
}

/**
 * The service's list of what it cannot answer, stated rather than drawn.
 *
 * `what` IS THE DESIGN'S OWN PHRASE FOR THE THING, deliberately, so that
 * somebody holding the design pack next to this screen can match them up
 * one for one instead of wondering which column went missing. Both strings come
 * off the response and neither is edited here.
 */
function Gaps({
  label,
  lede,
  gaps,
}: {
  readonly label: string;
  readonly lede: string;
  readonly gaps: readonly AttendeeGap[];
}): ReactNode {
  return (
    <section>
      <SecLabel>{label}</SecLabel>
      <div className="panel panel-stack">
        <p>{lede}</p>
        {gaps.length === 0 ? null : (
          <ul className="reasons">
            {gaps.map((gap) => (
              <li key={gap.what}>
                <b className="people-gap">{gap.what}.</b> {gap.why}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- small things

/**
 * The channel a registration arrived on, in the reader's words.
 *
 * THREE, WHERE THE DESIGN DREW TWO. Its Lane column offers "Onsite tablet" and
 * "Web", and the audit names this exactly: the design's two labels collapse our
 * three. A browser opened at the onsite address is at the stand and is not the
 * public page with the mailbox step removed, and folding it in with either of
 * the others would make a registration taken at the counter look like one taken
 * at home.
 *
 * An unrecognised channel is drawn as whatever the service said. A word this
 * console has no label for is still a fact, and mapping it to the nearest known
 * one is how a fourth channel gets reported as a third.
 *
 * THE WORDS ARE THE SERVICE'S (ATTENDEE_LANE_WORDS), because the service sorts
 * this column by them: a Lane head that ordered by the stored word would put
 * "Web, with a passcode" between "Onsite web" and "Stand tablet".
 */
const LANE = ATTENDEE_LANE_WORDS;

function Lane({ of }: { readonly of: string | null }): ReactNode {
  if (of === null) return <span className="quiet">not recorded</span>;
  return <>{LANE[of] ?? of}</>;
}

const SURFACE: Readonly<Record<string, string>> = {
  station: "A station on the stand",
  fixture: "A fixture in the hall",
  attendee_device: "Their own device",
};

const CAPTURE_MODE: Readonly<Record<string, string>> = {
  self_serve: "They filled it in themselves",
  assisted: "A crew member helped",
};

const ASSURANCE: Readonly<Record<string, string>> = {
  self_asserted: "Self asserted: somebody typed the address and nothing was sent to it",
  mailbox_verified: "Mailbox proved: a passcode sent to the address was returned",
};

/** A controlled word in the reader's language, or the word itself, or nothing. */
function Word({
  of,
  words,
}: {
  readonly of: string | null;
  readonly words: Readonly<Record<string, string>>;
}): ReactNode {
  if (of === null) return <span className="quiet">not recorded</span>;
  return <>{words[of] ?? of}</>;
}

/**
 * Whether a passcode sent to this mailbox ever came back.
 *
 * NOT THE DESIGN'S "VERIFIED" TAG, and the distance between the two is the point.
 * Its State column draws Verified in green against everybody who is not pending,
 * which on this service would put a green tag on a self-asserted address that
 * nobody has ever delivered anything to. What is recorded is a moment, and a
 * missing moment is drawn as the ordinary thing it usually is rather than as a
 * warning: `identity_assurance` is `self_asserted` by construction on every
 * stand tablet, so a whole show of amber tags would be a whole show of people
 * reading a fault into how registrations were taken.
 */
function Address({
  proved,
  assurance,
}: {
  readonly proved: string | null;
  readonly assurance: string | null;
}): ReactNode {
  if (proved !== null) return <span className="tag tag-good">Proved</span>;
  if (assurance === "mailbox_verified") {
    // Assurance says the mailbox was proved and no moment came with it. The two
    // are stored side by side rather than derived from each other, so they can
    // disagree, and a screen that quietly believed one of them would be picking
    // which. Both are on the record above; here it is drawn as the open question
    // it is.
    return <span className="tag tag-warn">Proved, no moment recorded</span>;
  }
  return <span className="tag tag-off">Not proved</span>;
}

/**
 * The latest word on one purpose.
 *
 * `undefined` IS NOT "NO" AND IS NOT DRAWN AS ONE. The design's column has four
 * values and one of them is "No"; there is no event for a refusal, so nothing
 * here can distinguish a purpose nobody was asked about from one everybody
 * declined. The words are the audit's reading: unknown, never no.
 */
function Consent({ word }: { readonly word: ConsentWord | undefined }): ReactNode {
  // The design's plain words rather than tags: a consent is read, not flagged.
  if (word === "granted") return <span>Agreed</span>;
  if (word === "withdrawn") return <span>Taken back</span>;
  return <span className="quiet">no event either way</span>;
}

const PURPOSE: Readonly<Record<string, string>> = {
  contact: "Contact",
  image_capture: "Being photographed",
  gallery_display: "Shown on a gallery screen",
  client_data_transfer: "Client transfer",
  marketing: "Marketing",
  marketing_email: "Marketing by email",
  marketing_sms: "Marketing by text message",
  marketing_phone: "Marketing by telephone",
  enquiry_follow_up: "Enquiry follow-up",
  image_marketing: "Their picture used in marketing",
  profiling: "Profiling",
};

function purposeRead(purpose: string): string {
  return PURPOSE[purpose] ?? purpose;
}

const METHOD: Readonly<Record<string, string>> = {
  tap: "tapped",
  signature: "signed",
  verbal_recorded_by_crew: "said aloud, recorded by crew",
  attendee_request: "at their own request",
  crew_action: "by a crew member",
  data_request: "through a data request",
};

function methodRead(method: string): string {
  return METHOD[method] ?? method;
}

/** A value, or the fact that the registration carries none. */
function or(value: string | null): ReactNode {
  if (value === null) return <span className="quiet">not recorded</span>;
  return <>{value}</>;
}

/** A version the service sent, or the fact that it sent none. Never a zero. */
function number(value: number | null): ReactNode {
  if (value === null) return <span className="quiet">not recorded</span>;
  return <>{value}</>;
}

/**
 * A timestamp in the reader's own zone, or the fact that none was sent.
 *
 * An unparseable string is drawn as what the service said rather than as the
 * word for nothing, which is Ride.tsx's rule and present.ts's before it: the
 * service did say something, and whoever has to work out why wants to see it.
 */
function when(value: string | null): ReactNode {
  if (value === null || value === "") return <span className="quiet">not recorded</span>;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}
